import ol = require("ol");
import MapView = require("../views/MapView");
import Portal = require("portal-gis");
import Utils = require("../utils");
import globals = require("../../globals");

var styleDefaults = {
    strokeColor: "rgba(255, 204, 0, 0.2)",
    fillColor: "rgba(255, 153, 0, 0.4)"
};

var proxy = (globals.proxy) ? url => `${globals.proxy}${encodeURI(url)}` : url => url;

class KmlStyles {

    static radius(radius = 5, fillColor = styleDefaults.fillColor, strokeColor = styleDefaults.strokeColor, strokeWidth = 1) {

        var defaultStyle = [new ol.style.Style({
            image: new ol.style.Circle({
                radius: radius,
                fill: new ol.style.Fill({
                    color: fillColor
                }),
                stroke: new ol.style.Stroke({
                    color: strokeColor,
                    width: strokeWidth
                })
            })
        })];

        return (feature, resolution) => {
            return defaultStyle;
        };
    }
}

function getTileOptions(l: Portal.MapLayerLeaf) {

    var options = Utils.asDictionary(l.Options.Values);

    var tileOptions = <olx.layer.TileOptions>{
        visible: "false" !== options["visible"],
        id: l.id,
        title: l.text,
        type: "base",
        componentType: l.componentType,
        assetType: l.assetType
    };
    return tileOptions;
}

class Handler {

    static about = `
Creates a layer suitable for rendering imagery provided by OSM and Bing as well support for KML.  These are the sources provided natively by OpenLayers.

Usage Example: 

    <LAYER basemap="True" id="mapquest-osm" maxlevel="20" minlevel="10" text="Map Quest" type="app/layer-factory/native">
    <OPTIONS>
        <Values id="layerType" value="osm" />
        <Values id="layerStyle" value="osm" />
        <Values id="visible" value="false" />
    </OPTIONS>
    </LAYER>

    `;

    static options = {
        "layerType": {
            "about": `Identifies the layer type to be configured`,
            "domain": "bing|debug|kml|stamen|xyz",
            "default": "osm",
        },
        "layerStyle": {
            "about": "For bing one of Aerial,AerialWithLabels,Road,RoadOnDemand.  For stamen one of terrain,toner,watercolor",
        },
        "key": {
            "about": "The API key for bing",
        },
        "visible": {
            "about": `controls the initial visibility of a layer, true to show the layer immediately, false otherwise`,
            "domain": "true|false",
            "default": "true",
        },
        "srs": {
            "about": "For KML, the coordinate system of the KML document"
        },
        "url": {
            "about": "For KML, the url if no layer url is provided"
        },
    }

    private static xyz(l: Portal.MapLayerLeaf) {

        var options = Utils.asDictionary(l.Options.Values);

        var sourceOptions = <olx.source.XYZOptions>{};

        if (typeof l.maxlevel !== "undefined") sourceOptions.maxZoom = l.maxlevel;

        if (l.url) sourceOptions.url = l.url;
        if (l.minlevel) sourceOptions.minZoom = l.minlevel;
        if (l.maxlevel) sourceOptions.maxZoom = l.maxlevel;

        var source = new ol.source.XYZ(sourceOptions);

        var tileOptions = getTileOptions(l);
        tileOptions.source = source;

        var tile = new ol.layer.Tile(tileOptions);

        return tile;
    }

    private static osm(l: Portal.MapLayerLeaf) {

        var options = Utils.asDictionary(l.Options.Values);

        var sourceOptions = <olx.source.OSMOptions>{};

        //if (typeof l.maxlevel !== "undefined") sourceOptions.maxZoom = l.maxlevel;
        if (l.url) sourceOptions.url = l.url;

        var source = new ol.source.OSM(sourceOptions);

        var tileOptions = getTileOptions(l);
        tileOptions.source = source;

        var tile = new ol.layer.Tile(tileOptions);

        return tile;
    }

    private static bing(l: Portal.MapLayerLeaf) {

        var options = Utils.asDictionary(l.Options.Values);

        var sourceOptions = <olx.source.BingMapsOptions>{};
        sourceOptions.imagerySet = "AerialWithLabels";

        //if (typeof l.maxlevel !== "undefined") sourceOptions.maxZoom = l.maxlevel;

        Utils.ifOption(options, "layerStyle", layerStyle => sourceOptions.imagerySet = layerStyle);

        Utils.ifOption(options, "key", key => sourceOptions.key = key);

        var source = new ol.source.BingMaps(sourceOptions);

        var tileOptions = getTileOptions(l);
        tileOptions.source = source;

        var tile = new ol.layer.Tile(tileOptions);

        return tile;
    }

    private static stamen(l: Portal.MapLayerLeaf) {

        var options = Utils.asDictionary(l.Options.Values);

        var sourceOptions = <olx.source.StamenOptions>{};
        sourceOptions.layer = "watercolor";

        //if (typeof l.maxlevel !== "undefined") sourceOptions.maxZoom = l.maxlevel;

        if (l.url) sourceOptions.url = l.url;
        if (l.minlevel) sourceOptions.minZoom = l.minlevel;
        if (l.maxlevel) sourceOptions.maxZoom = l.maxlevel;

        Utils.ifOption(options, "layerStyle", layerStyle => sourceOptions.layer = layerStyle);

        var source = new ol.source.Stamen(sourceOptions);

        var tileOptions = getTileOptions(l);
        tileOptions.source = source;

        var tile = new ol.layer.Tile(tileOptions);

        return tile;
    }

    private static kml(l: Portal.MapLayerLeaf) {

        let options = Utils.asDictionary(l.Options.Values);

        let sourceOptions = <olx.source.VectorOptions>{
            format: new ol.format.KML(),
            projection: new ol.proj.Projection({ code: options["srs"] || "EPSG:4326" }),
            url: proxy(l.url || options["url"])
        };

        let source = new ol.source.Vector(sourceOptions);

        let tileOptions = <olx.layer.VectorOptions>getTileOptions(l);
        tileOptions.source = source;
        delete tileOptions["type"];

        var tile = new ol.layer.Vector(tileOptions);

        return tile;
    }

    private static debug(l: Portal.MapLayerLeaf) {

        var options = Utils.asDictionary(l.Options.Values);

        var sourceOptions = <olx.source.TileDebugOptions>{};

        var source = new ol.source.TileDebug(sourceOptions);
        delete source.type;

        var tileOptions = getTileOptions(l);
        tileOptions.source = source;

        var tile = new ol.layer.Tile(tileOptions);

        return tile;
    }

    static create(args: { layer: Portal.MapLayerLeaf; view: MapView }): ol.layer.Layer {
        let l = args.layer;
        let options = Utils.asDictionary(l.Options.Values);
        let layerType = options["layerType"] || Handler.options.layerType.default;
        let factory = Handler[layerType];
        return factory && factory(l);
    }

}

export = Handler;